/**
 * <AppTile /> — single Pulse app card on the launcher grid.
 *
 * Click → hard navigation to the app's production origin. We deliberately
 * use `window.location.assign` (not <a href>) so per-app middleware re-checks
 * access on the new origin even when a stale cookie says otherwise.
 *
 * Chunk 5 task 5.5 — minimal version: name, lucide icon, role badge, "Open".
 * The richer status-chip / analytics wiring lives in tasks 5.7 / 5.13.
 */

import {
  LayoutGrid,
  Network,
  ListChecks,
  Smartphone,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import type { PulseApp, PulseRoleSlug } from "@pulse-business-ai/types";

export type LauncherApp = PulseApp | "pulse-app";

export interface AppTileProps {
  readonly app: LauncherApp;
  readonly role?: PulseRoleSlug;
  readonly onOpen?: (app: LauncherApp) => void;
}

interface AppMeta {
  readonly label: string;
  readonly description: string;
  readonly url: string;
  readonly icon: LucideIcon;
}

const APP_META: Record<LauncherApp, AppMeta> = {
  admin: {
    label: "IT Admin",
    description: "Infrastructure, access, secrets, and platform administration.",
    url: "https://itadmin.pulsebusiness.ai",
    icon: LayoutGrid,
  },
  hub: {
    label: "AI Hub",
    description: "Live agent operations, dispatch, incidents, and control plane.",
    url: "https://aihub.pulsebusiness.ai",
    icon: Network,
  },
  projects: {
    label: "Business OS",
    description: "Goals, tasks, departments, sprints, meetings, and delivery planning.",
    url: "https://businessos.pulsebusiness.ai",
    icon: ListChecks,
  },
  "pulse-app": {
    label: "RestaurantOS",
    description: "The main Pulse workspace for daily business activity.",
    url: "https://restaurantos.pulsebusiness.ai",
    icon: Smartphone,
  },
  launcher: {
    label: "Launcher",
    description: "You are here.",
    url: "https://start.pulsebusiness.ai",
    icon: Sparkles,
  },
};

const ROLE_TONE: Record<PulseRoleSlug, string> = {
  admin: "var(--pulse-blue-500, var(--blue-500))",
  member: "var(--pulse-teal-500, #14b8a6)",
  viewer: "var(--pulse-slate-500, #64748b)",
};

export function AppTile({ app, role, onOpen }: AppTileProps): JSX.Element {
  const meta = APP_META[app];
  const Icon = meta.icon;

  const handleOpen = (): void => {
    onOpen?.(app);
    if (typeof window !== "undefined") {
      window.location.assign(meta.url);
    }
  };

  return (
    <article
      className="pulse-launcher-tile"
      data-app={app}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        padding: "1.25rem",
        borderRadius: "0.75rem",
        background: "var(--pulse-surface, #ffffff)",
        border: "1px solid var(--pulse-border, #e2e8f0)",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
          <Icon size={28} aria-hidden="true" />
          <h2 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 600 }}>
            {meta.label}
          </h2>
        </div>
        {role ? (
          <span
            data-testid={`role-badge-${app}`}
            aria-label={`Your role: ${role}`}
            style={{
              fontSize: "0.7rem",
              fontWeight: 600,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              padding: "0.2rem 0.55rem",
              borderRadius: "999px",
              color: "#fff",
              background: ROLE_TONE[role],
            }}
          >
            {role}
          </span>
        ) : null}
      </header>

      <p
        style={{
          margin: 0,
          fontSize: "0.875rem",
          lineHeight: 1.45,
          color: "var(--pulse-text-muted, #475569)",
        }}
      >
        {meta.description}
      </p>

      <button
        type="button"
        onClick={handleOpen}
        aria-label={`Open ${meta.label}`}
        style={{
          alignSelf: "flex-start",
          marginTop: "auto",
          padding: "0.45rem 0.95rem",
          fontSize: "0.875rem",
          fontWeight: 600,
          color: "#fff",
          background: "var(--pulse-blue-600, #2563eb)",
          border: "none",
          borderRadius: "0.5rem",
          cursor: "pointer",
        }}
      >
        Open
      </button>
    </article>
  );
}
